import { Add } from "@mui/icons-material"
import RestoreIcon from "@mui/icons-material/Restore"
import { Tooltip } from "@mui/material"
import {
  DataGridPro,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro"
import { deDE } from "@mui/x-data-grid/locales"
import { StyledButton } from "components/Button/StyledButton"
import { DeleteDialog } from "components/DeleteDialog/DeleteDialog"
import {
  Calculation,
  CalculationType,
  ExaminationConfig,
  Laboratory,
  Skill,
  TreoLocation,
} from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useCallback, useEffect, useState } from "react"
import { AddExamConfigDialog } from "./AddExamConfigDialog"
import ExaminationConfigDetailPanel from "./ExaminationConfigDetailPanel"

const calculationEmpty: Calculation = {
  id: "NEW",
  calculationType: CalculationType.TIME,
  offerText: "",
  humanPrice: {
    amount: 0,
    cost: 0,
    name: "",
    price: 0,
  },
  machinePrice: {
    amount: 0,
    cost: 0,
    name: "",
    price: 0,
  },
  connectionPrice: [],
}

const laboratoryEmpty: Laboratory = {
  id: "",
  name: "",
  manager: "",
}

const skillEmpty: Skill = {
  id: "",
  name: "",
  person: [],
  testEquipment: [],
}

const examConfigEmpty: ExaminationConfig = {
  id: "NEW",
  name: "",
  laboratory: laboratoryEmpty,
  skill: skillEmpty,
  eutDataGroups: [],
  tasks: [],
  article: "",
  isObsolete: false,
  defaultCalculation: calculationEmpty,
}

const AdminExaminationConfig = () => {
  const { examinationConfigs, articles } = useStoreState((state) => state)
  const {
    updateExaminationConfig,
    deleteExaminationConfig,
    fetchExaminationConfigs,
    setExaminationConfigsObsolete,
  } = useStoreActions((actions) => actions)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formState, setFormState] = useState<ExaminationConfig>(examConfigEmpty)
  const [openDelete, setOpenDelete] = useState(false)
  const [toDelete, setToDelete] = useState<string[]>([])
  const [showObsolete, setShowObsolete] = useState(false)

  useEffect(() => {
    fetchExaminationConfigs()
  }, [fetchExaminationConfigs])

  const handleEdit = (id: string) => {
    if (id === "NEW") {
      setFormState({
        ...examConfigEmpty,
        id: "NEW",
      })
    } else {
      const exConfig = examinationConfigs.find((ex) => ex.id === id)
      if (exConfig) {
        setFormState(exConfig)
      }
    }
    setDialogOpen(true)
  }

  const handleDelete = (id: string[]) => {
    setOpenDelete(true)
    setToDelete(id)
  }

  const handleDeleteSelected = async () => {
    await deleteExaminationConfig(toDelete)
    setToDelete([])
    setOpenDelete(false)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  const handleFormSubmit = (arg: ExaminationConfig) => {
    updateExaminationConfig(arg)
    setDialogOpen(false)
  }

  const columns = [
    { field: "name", headerName: "Bezeichnung", flex: 300 },
    {
      field: "laboratory",
      headerName: "Laborbereich",
      flex: 300,
      valueGetter: (value: Laboratory) => (value ? value.name : ""),
    },
    {
      field: "location",
      headerName: "Standort",
      flex: 300,
      valueGetter: (value: TreoLocation) => (value ? value.name : ""),
    },
    {
      field: "article",
      headerName: "Artikel",
      flex: 300,
      valueGetter: (value: string) => {
        const article = articles.find((article) => article.inventoryID === value)

        return article ? article.description : value
      },
    },
    {
      field: "calculationType",
      headerName: "Calculation Type",
      flex: 300,
      valueGetter: (value: CalculationType) => {
        return value === CalculationType.TIME ? "Zeit" : "Menge"
      },
      //hidden by default but can be shown by setting in the filter panel
      hide: true,
    },
  ]

  const getDetailPanelContent = useCallback(({ row }: { row: ExaminationConfig }) => {
    return <ExaminationConfigDetailPanel row={row} />
  }, [])

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={showObsolete}
            onChange={() => setShowObsolete((v) => !v)}
          />
          Gelöschte anzeigen
        </label>
      </div>
      <DataGridPro
        getRowId={(row) => row.id}
        rows={showObsolete ? examinationConfigs : examinationConfigs.filter((c) => !c.isObsolete)}
        columns={[
          ...columns,
          {
            field: "actions",
            headerName: "Actions",
            type: "actions",
            width: 150,
            renderCell: (params) => (
              <>
                <StyledButton
                  buttonType="edit"
                  iconOnly={true}
                  onClick={() => handleEdit(params.row.id)}
                  size="small"
                />
                {params.row.isObsolete ? (
                  <Tooltip title="Wiederherstellen">
                    <StyledButton
                      buttonType="custom"
                      customIcon={<RestoreIcon fontSize="small" />}
                      iconOnly={true}
                      onClick={async () => {
                        await setExaminationConfigsObsolete({
                          ids: [params.row.id],
                          obsolete: false,
                        })
                      }}
                      size="small"
                    />
                  </Tooltip>
                ) : (
                  <StyledButton
                    buttonType="delete"
                    iconOnly={true}
                    onClick={() => handleDelete([params.row.id])}
                    size="small"
                  />
                )}
              </>
            ),
          },
        ]}
        initialState={{
          columns: {
            columnVisibilityModel: {
              calculationType: false, // Force hide this column
            },
          },
        }}
        checkboxSelection={false}
        disableMultipleRowSelection={true}
        slots={{
          toolbar: () => (
            <GridToolbarContainer>
              <GridToolbarColumnsButton />
              <GridToolbarFilterButton />
              <GridToolbarDensitySelector />
              <StyledButton
                buttonType="custom"
                customIcon={<Add />}
                onClick={() => handleEdit("NEW")}
              >
                Hinzufügen
              </StyledButton>
              <GridToolbarQuickFilter />
            </GridToolbarContainer>
          ),
        }}
        // Removed: onRowSelectionModelChange={(value) => { setSelectedRows(value) }}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={() => "auto"}
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
      />

      <AddExamConfigDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        formState={formState}
        onSubmit={handleFormSubmit}
      />

      <DeleteDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onDelete={handleDeleteSelected}
      />
    </>
  )
}

export default AdminExaminationConfig
